import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
    Phone,
    MessageCircle,
    ChevronDown,
    Plane,
    CarFront,
    Crown,
    Wifi,
    Armchair,
    CupSoda,
    Snowflake,
    Languages,
    Baby,
    Clock3,
    Radar,
    BadgeCheck,
    UserCheck,
    Sparkles,
    ShieldCheck,
    ArrowRight,
    Plus,
    Minus,
    Quote,
} from "lucide-react";
import { useState } from "react";
import { useLang, PHONE_TEL, waLink } from "@/i18n";
import { SEO } from "@/components/SEO";
import { Marquee } from "@/components/Marquee";
import { QuickBooking } from "@/components/QuickBooking";
import { EktirenBadge } from "@/components/EktirenBadge";
import { Reveal } from "@/components/Reveal";

const IMGS = {
    hero: "https://images.unsplash.com/photo-1618373444305-3a77b7719a59?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
    front: "https://images.pexels.com/photos/9459158/pexels-photo-9459158.jpeg?auto=compress&cs=tinysrgb&w=940",
    side: "https://images.pexels.com/photos/19864909/pexels-photo-19864909.jpeg?auto=compress&cs=tinysrgb&w=940",
    interior: "https://images.pexels.com/photos/39416592/pexels-photo-39416592.png?auto=compress&cs=tinysrgb&w=940",
    harbor: "https://images.pexels.com/photos/12861106/pexels-photo-12861106.jpeg?auto=compress&cs=tinysrgb&w=940",
    marina: "https://images.pexels.com/photos/14909610/pexels-photo-14909610.jpeg?auto=compress&cs=tinysrgb&w=940",
    airport: "https://images.unsplash.com/photo-1655827763440-7905302b75ff?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
    chauffeur: "https://images.pexels.com/photos/7594130/pexels-photo-7594130.jpeg?auto=compress&cs=tinysrgb&w=940",
};

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.95, delay, ease: [0.16, 1, 0.3, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

const Hero = () => {
    const { t } = useLang();
    const { scrollY } = useScroll();
    const y = useTransform(scrollY, [0, 700], [0, 160]);
    const opacity = useTransform(scrollY, [0, 500], [1, 0.35]);

    return (
        <section className="relative min-h-[100svh] flex items-center overflow-hidden" data-testid="hero-section">
            <motion.div style={{ y, opacity }} className="absolute inset-0">
                <img
                    src={IMGS.hero}
                    alt="Mercedes E-Class VIP Taksi - Gece"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#08080A]/80 via-[#08080A]/55 to-[#08080A]" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#08080A]/85 via-transparent to-[#08080A]/40" />
            </motion.div>

            <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 w-full pt-32 pb-40">
                <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.8 }}
                    className="ff-sans text-[11px] md:text-xs tracking-[0.4em] text-[#D4AF37] uppercase block mb-6"
                    data-testid="hero-eyebrow"
                >
                    {t("hero.eyebrow")}
                </motion.span>

                <h1 className="ff-serif text-4xl sm:text-5xl lg:text-7xl text-[#F7F5F0] leading-[1.08] tracking-tight max-w-3xl" data-testid="hero-title">
                    <MaskedLine delay={0.35}>{t("hero.line1")}</MaskedLine>
                    <MaskedLine delay={0.5}>
                        <span className="italic text-gold-gradient">{t("hero.line2")}</span>
                    </MaskedLine>
                    <MaskedLine delay={0.65}>{t("hero.line3")}</MaskedLine>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1, duration: 0.8 }}
                    className="mt-7 max-w-xl text-base md:text-lg text-zinc-400 font-light ff-sans leading-relaxed"
                    data-testid="hero-subtitle"
                >
                    {t("hero.sub")}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.2, duration: 0.8 }}
                    className="mt-10 flex flex-col sm:flex-row gap-4"
                >
                    <a
                        href={PHONE_TEL}
                        data-testid="hero-call-button"
                        className="gold-glow-hover flex items-center justify-center gap-2.5 rounded-full bg-[#D4AF37] text-[#08080A] font-semibold px-8 py-4 ff-sans text-sm tracking-wide"
                    >
                        <Phone size={16} /> {t("hero.ctaCall")}
                    </a>
                    <a
                        href={waLink(t("hero.waMsg"))}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="whatsapp-booking-button"
                        className="gold-glow-hover flex items-center justify-center gap-2.5 rounded-full border border-[#D4AF37]/50 text-[#E6C687] px-8 py-4 ff-sans text-sm tracking-wide backdrop-blur-sm bg-[#08080A]/40"
                    >
                        <MessageCircle size={16} /> {t("hero.ctaWa")}
                    </a>
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8 }}
                className="absolute bottom-28 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-zinc-500"
            >
                <span className="ff-sans text-[10px] tracking-[0.4em] uppercase">{t("hero.scroll")}</span>
                <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
                    <ChevronDown size={18} className="text-[#D4AF37]" />
                </motion.span>
            </motion.div>
        </section>
    );
};

const Stats = () => {
    const { t } = useLang();
    const stats = t("stats");
    return (
        <section className="max-w-6xl mx-auto px-5 lg:px-8 mt-14" data-testid="stats-strip">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 rounded-2xl overflow-hidden border border-white/5">
                {Array.isArray(stats) &&
                    stats.map((s, i) => (
                        <Reveal key={s.label} delay={i * 0.08} className="bg-[#0C0C10] px-6 py-8 text-center">
                            <span className="ff-serif text-3xl md:text-4xl text-gold-gradient block">{s.value}</span>
                            <span className="ff-sans text-[11px] tracking-[0.25em] uppercase text-zinc-500 mt-2 block">
                                {s.label}
                            </span>
                        </Reveal>
                    ))}
            </div>
        </section>
    );
};

const Statement = () => {
    const { t } = useLang();
    return (
        <section className="max-w-7xl mx-auto px-5 lg:px-8 py-28 grid lg:grid-cols-2 gap-14 items-center" data-testid="brand-statement">
            <Reveal>
                <span className="ff-sans text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] block mb-4">
                    {t("statement.label")}
                </span>
                <h2 className="ff-serif text-3xl md:text-5xl text-[#F7F5F0] leading-[1.15] tracking-tight">
                    {t("statement.title")}
                </h2>
                <p className="mt-6 text-base md:text-lg text-zinc-400 font-light ff-sans leading-relaxed max-w-lg">
                    {t("statement.text")}
                </p>
            </Reveal>
            <Reveal delay={0.15}>
                <div className="spotlight-frame">
                    <img
                        src={IMGS.harbor}
                        alt="Girne Limanı - Kyrenia Harbour"
                        loading="lazy"
                        className="relative z-10 rounded-2xl border border-[#D4AF37]/20 object-cover w-full h-[420px]"
                    />
                </div>
            </Reveal>
        </section>
    );
};

const serviceIcons = [Plane, CarFront, Crown];
const serviceLinks = ["/ercan-transfer", "/girne-taksi", null];
const serviceImgs = [IMGS.airport, IMGS.marina, IMGS.chauffeur];

const Services = () => {
    const { t } = useLang();
    const items = t("services.items");
    return (
        <section id="hizmetler" className="py-24 bg-[#0A0A0E] border-y border-white/5" data-testid="services-section">
            <div className="max-w-7xl mx-auto px-5 lg:px-8">
                <Reveal className="max-w-2xl mb-14">
                    <span className="ff-sans text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] block mb-4">
                        {t("services.label")}
                    </span>
                    <h2 className="ff-serif text-3xl md:text-5xl text-[#F7F5F0] tracking-tight">{t("services.title")}</h2>
                    <p className="mt-5 text-base md:text-lg text-zinc-400 font-light ff-sans">{t("services.sub")}</p>
                </Reveal>

                <div className="grid md:grid-cols-3 gap-6">
                    {Array.isArray(items) &&
                        items.map((s, i) => {
                            const Icon = serviceIcons[i];
                            const to = serviceLinks[i];
                            const Card = (
                                <div className="gold-glow-hover group h-full rounded-2xl border border-white/8 bg-[#101014] overflow-hidden flex flex-col" data-testid={`service-card-${i}`}>
                                    <div className="relative h-48 overflow-hidden">
                                        <img
                                            src={serviceImgs[i]}
                                            alt={s.title}
                                            loading="lazy"
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#101014] to-transparent" />
                                    </div>
                                    <div className="p-7 flex flex-col flex-1">
                                        <Icon size={22} className="text-[#D4AF37] mb-4" />
                                        <h3 className="ff-serif text-2xl text-[#F7F5F0] mb-3">{s.title}</h3>
                                        <p className="text-sm text-zinc-400 font-light ff-sans leading-relaxed flex-1">{s.desc}</p>
                                        <span className="mt-6 inline-flex items-center gap-2 text-[13px] tracking-[0.15em] uppercase ff-sans text-[#E6C687]">
                                            {s.link} <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                                        </span>
                                    </div>
                                </div>
                            );
                            return (
                                <Reveal key={s.title} delay={i * 0.1}>
                                    {to ? (
                                        <Link to={to} data-testid={`service-link-${i}`} className="block h-full">{Card}</Link>
                                    ) : (
                                        <a href={waLink(t("hero.waMsg"))} target="_blank" rel="noopener noreferrer" data-testid={`service-link-${i}`} className="block h-full">{Card}</a>
                                    )}
                                </Reveal>
                            );
                        })}
                </div>
            </div>
        </section>
    );
};

const specIcons = [Wifi, Armchair, CupSoda, Snowflake, Languages, Baby];

const Fleet = () => {
    const { t } = useLang();
    const specs = t("fleet.specs");
    return (
        <section id="filo" className="py-24" data-testid="fleet-section">
            <div className="max-w-7xl mx-auto px-5 lg:px-8">
                <Reveal className="text-center max-w-2xl mx-auto mb-14">
                    <span className="ff-sans text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] block mb-4">
                        {t("fleet.label")}
                    </span>
                    <h2 className="ff-serif text-3xl md:text-5xl text-[#F7F5F0] tracking-tight">{t("fleet.title")}</h2>
                    <p className="mt-5 text-base md:text-lg text-zinc-400 font-light ff-sans">{t("fleet.sub")}</p>
                </Reveal>

                <div className="grid lg:grid-cols-3 gap-6">
                    {[IMGS.front, IMGS.side, IMGS.interior].map((src, i) => (
                        <Reveal key={src} delay={i * 0.12}>
                            <div className="spotlight-frame group" data-testid={`fleet-image-${i}`}>
                                <img
                                    src={src}
                                    alt={i === 2 ? "Mercedes E-Class İç Mekân" : "Mercedes E-Class Dış Görünüm"}
                                    loading="lazy"
                                    className="relative z-10 w-full h-72 lg:h-[420px] object-cover rounded-2xl border border-[#D4AF37]/20 transition-transform duration-700 group-hover:scale-[1.02]"
                                />
                            </div>
                        </Reveal>
                    ))}
                </div>

                <div className="mt-12 flex flex-wrap justify-center gap-3">
                    {Array.isArray(specs) &&
                        specs.map((s, i) => {
                            const Icon = specIcons[i];
                            return (
                                <Reveal key={s} delay={i * 0.05}>
                                    <span
                                        className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/25 bg-[#0F0F13] px-5 py-2.5 text-[13px] ff-sans text-zinc-300"
                                        data-testid={`fleet-spec-${i}`}
                                    >
                                        {Icon && <Icon size={14} className="text-[#D4AF37]" />} {s}
                                    </span>
                                </Reveal>
                            );
                        })}
                </div>
            </div>
        </section>
    );
};

const Routes = () => {
    const { t } = useLang();
    const items = t("routes.items");
    return (
        <section id="fiyatlar" className="py-24 bg-[#0A0A0E] border-y border-white/5" data-testid="routes-section">
            <div className="max-w-7xl mx-auto px-5 lg:px-8">
                <Reveal className="max-w-2xl mb-14">
                    <span className="ff-sans text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] block mb-4">
                        {t("routes.label")}
                    </span>
                    <h2 className="ff-serif text-3xl md:text-5xl text-[#F7F5F0] tracking-tight">{t("routes.title")}</h2>
                    <p className="mt-5 text-base md:text-lg text-zinc-400 font-light ff-sans">{t("routes.sub")}</p>
                </Reveal>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {Array.isArray(items) &&
                        items.map((r, i) => (
                            <Reveal key={`${r.from}-${r.to}`} delay={(i % 4) * 0.08}>
                                <div
                                    className="gold-glow-hover rounded-2xl border border-white/8 bg-[#101014] p-6 flex flex-col h-full"
                                    data-testid={`route-card-${i}`}
                                >
                                    <div className="flex items-center gap-2 text-[11px] ff-sans tracking-[0.2em] uppercase text-zinc-500 mb-4">
                                        <Clock3 size={13} className="text-[#D4AF37]" /> {t("routes.duration")} {r.time}
                                    </div>
                                    <div className="ff-serif text-xl text-[#F7F5F0] leading-snug flex-1">
                                        {r.from}
                                        <span className="text-[#D4AF37] mx-1.5">→</span>
                                        {r.to}
                                    </div>
                                    <p className="text-[11px] ff-sans text-zinc-500 mt-3 mb-5">{t("routes.fixed")}</p>
                                    <a
                                        href={waLink(t("routes.waMsg")(`${r.from} → ${r.to}`))}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`route-price-button-${i}`}
                                        className="gold-glow-hover mt-auto text-center rounded-full border border-[#D4AF37]/45 text-[#E6C687] text-[13px] ff-sans tracking-[0.12em] uppercase py-2.5"
                                    >
                                        {t("routes.cta")}
                                    </a>
                                </div>
                            </Reveal>
                        ))}
                </div>
            </div>
        </section>
    );
};

const whyIcons = [Clock3, Radar, BadgeCheck, UserCheck, Sparkles, ShieldCheck];

const Why = () => {
    const { t } = useLang();
    const items = t("why.items");
    return (
        <section className="py-24" data-testid="why-section">
            <div className="max-w-7xl mx-auto px-5 lg:px-8">
                <Reveal className="max-w-2xl mb-14">
                    <span className="ff-sans text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] block mb-4">
                        {t("why.label")}
                    </span>
                    <h2 className="ff-serif text-3xl md:text-5xl text-[#F7F5F0] tracking-tight">{t("why.title")}</h2>
                </Reveal>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {Array.isArray(items) &&
                        items.map((w, i) => {
                            const Icon = whyIcons[i];
                            return (
                                <Reveal key={w.title} delay={(i % 3) * 0.08}>
                                    <div className="gold-glow-hover rounded-2xl border border-white/8 bg-[#0F0F13] p-7 h-full" data-testid={`why-card-${i}`}>
                                        {Icon && <Icon size={22} className="text-[#D4AF37] mb-4" />}
                                        <h3 className="ff-serif text-xl text-[#F7F5F0] mb-2">{w.title}</h3>
                                        <p className="text-sm text-zinc-400 font-light ff-sans leading-relaxed">{w.desc}</p>
                                    </div>
                                </Reveal>
                            );
                        })}
                </div>
            </div>
        </section>
    );
};

const Testimonials = () => {
    const { t } = useLang();
    const items = t("testimonials.items");
    return (
        <section className="py-24 bg-[#0A0A0E] border-y border-white/5" data-testid="testimonials-section">
            <div className="max-w-7xl mx-auto px-5 lg:px-8">
                <Reveal className="text-center max-w-xl mx-auto mb-14">
                    <span className="ff-sans text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] block mb-4">
                        {t("testimonials.label")}
                    </span>
                    <h2 className="ff-serif text-3xl md:text-5xl text-[#F7F5F0] tracking-tight">{t("testimonials.title")}</h2>
                </Reveal>
                <div className="grid md:grid-cols-3 gap-6">
                    {Array.isArray(items) &&
                        items.map((r, i) => (
                            <Reveal key={r.name} delay={i * 0.1}>
                                <figure className="gold-glow-hover rounded-2xl border border-white/8 bg-[#101014] p-8 h-full flex flex-col" data-testid={`testimonial-card-${i}`}>
                                    <Quote size={20} className="text-[#D4AF37] mb-5" />
                                    <blockquote className="text-sm text-zinc-300 font-light ff-sans leading-relaxed flex-1">
                                        "{r.text}"
                                    </blockquote>
                                    <figcaption className="mt-6 flex items-center justify-between">
                                        <span className="ff-sans text-sm text-[#E6C687]">{r.name}</span>
                                        <span className="ff-sans text-[10px] tracking-[0.25em] uppercase text-zinc-500">{r.tag}</span>
                                    </figcaption>
                                </figure>
                            </Reveal>
                        ))}
                </div>
            </div>
        </section>
    );
};

const Faq = () => {
    const { t } = useLang();
    const items = t("faq.items");
    const [open, setOpen] = useState(0);
    return (
        <section className="py-24" data-testid="faq-section">
            <div className="max-w-3xl mx-auto px-5 lg:px-8">
                <Reveal className="text-center mb-12">
                    <span className="ff-sans text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] block mb-4">
                        {t("faq.label")}
                    </span>
                    <h2 className="ff-serif text-3xl md:text-5xl text-[#F7F5F0] tracking-tight">{t("faq.title")}</h2>
                </Reveal>
                <div className="space-y-3">
                    {Array.isArray(items) &&
                        items.map((f, i) => (
                            <Reveal key={f.q} delay={i * 0.05}>
                                <div className="rounded-xl border border-white/8 bg-[#0F0F13] overflow-hidden" data-testid={`faq-item-${i}`}>
                                    <button
                                        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                                        onClick={() => setOpen(open === i ? -1 : i)}
                                        data-testid={`faq-toggle-${i}`}
                                    >
                                        <span className="ff-sans text-sm md:text-base text-[#F7F5F0]">{f.q}</span>
                                        {open === i ? (
                                            <Minus size={16} className="text-[#D4AF37] shrink-0" />
                                        ) : (
                                            <Plus size={16} className="text-[#D4AF37] shrink-0" />
                                        )}
                                    </button>
                                    {open === i && (
                                        <p className="px-6 pb-6 text-sm text-zinc-400 font-light ff-sans leading-relaxed" data-testid={`faq-answer-${i}`}>
                                            {f.a}
                                        </p>
                                    )}
                                </div>
                            </Reveal>
                        ))}
                </div>
            </div>
        </section>
    );
};

const Contact = () => {
    const { t } = useLang();
    return (
        <section id="iletisim" className="py-24 bg-[#0A0A0E] border-t border-white/5 grain-overlay" data-testid="contact-section">
            <div className="relative max-w-5xl mx-auto px-5 lg:px-8 text-center">
                <Reveal>
                    <span className="ff-sans text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] block mb-4">
                        {t("contact.label")}
                    </span>
                    <h2 className="ff-serif text-3xl md:text-5xl text-[#F7F5F0] tracking-tight">{t("contact.title")}</h2>
                    <p className="mt-5 text-base md:text-lg text-zinc-400 font-light ff-sans max-w-xl mx-auto">{t("contact.sub")}</p>
                </Reveal>

                <div className="mt-12 grid sm:grid-cols-2 gap-5 max-w-3xl mx-auto">
                    <Reveal delay={0.1}>
                        <a
                            href={PHONE_TEL}
                            data-testid="contact-call-card"
                            className="gold-glow-hover block rounded-2xl bg-[#D4AF37] text-[#08080A] p-8 text-left"
                        >
                            <Phone size={26} className="mb-4" />
                            <span className="ff-sans text-xs uppercase tracking-[0.25em] block opacity-70">{t("contact.callTitle")}</span>
                            <span className="ff-serif text-2xl md:text-3xl block mt-2">+90 533 856 7070</span>
                            <span className="ff-sans text-sm block mt-2 opacity-80">{t("contact.callDesc")}</span>
                        </a>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <a
                            href={waLink(t("hero.waMsg"))}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="contact-whatsapp-card"
                            className="gold-glow-hover block rounded-2xl border border-[#25D366]/50 bg-[#101714] text-[#F7F5F0] p-8 text-left"
                        >
                            <MessageCircle size={26} className="mb-4 text-[#25D366]" />
                            <span className="ff-sans text-xs uppercase tracking-[0.25em] block text-zinc-400">{t("contact.waTitle")}</span>
                            <span className="ff-serif text-2xl md:text-3xl block mt-2">WhatsApp</span>
                            <span className="ff-sans text-sm block mt-2 text-zinc-400">{t("contact.waDesc")}</span>
                        </a>
                    </Reveal>
                </div>

                <Reveal delay={0.3}>
                    <div className="mt-12 rounded-2xl border border-white/8 bg-[#0F0F13] px-8 py-7 max-w-3xl mx-auto">
                        <span className="ff-sans text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] block mb-2">
                            {t("contact.area")}
                        </span>
                        <p className="text-sm text-zinc-400 ff-sans font-light" data-testid="contact-area-text">{t("contact.areaText")}</p>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

export default function Home() {
    const { t } = useLang();
    const location = useLocation();

    useEffect(() => {
        if (location.hash) {
            const el = document.querySelector(location.hash);
            if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 150);
        }
    }, [location]);

    return (
        <main>
            <SEO title={t("seo.home.title")} description={t("seo.home.desc")} path="/" />
            <Hero />
            <Marquee />
            <QuickBooking />
            <Stats />
            <Statement />
            <Services />
            <Fleet />
            <Routes />
            <Why />
            <Testimonials />
            <EktirenBadge />
            <Faq />
            <Contact />
        </main>
    );
}
